import React from 'react';
import styled from '@emotion/styled/macro';
import AutomatedListingTileBlock from '@blocks/automated-listing-tile-block/automated-listing-tile-block';
import CircularExamplesBlock from '@blocks/circular-examples-block/circular-examples-block';
import EmbedBlock from '@blocks/embed-block/embed-block';
import GlossaryTooltipText from '@atoms/glossary-tooltip-atoms/glossary-tooltip-atoms';
import HighlightBlock from '@blocks/highlight-block/highlight-block';
import InteractiveImageBlock from '@blocks/interactive-image-block/interactive-image-block';
import TileBlock from '@blocks/tile-block/tile-block';
import CallToActionBlock from '@blocks/call-to-action-block/call-to-action-block';
import ImageBlock from '@blocks/image-block/image-block';
import { getGtmLinkId } from '@utils/utils';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import { FlexibleLinkAtom } from '@atoms/link-atoms/link-atoms';
import { DashedHrAtom } from '@atoms/hr-atoms/hr-atoms';
import {
  OrderedListAtom,
  UnorderedListAtom,
} from '@atoms/list-atoms/list-atoms';
import MediaBlock, {
  RichTextEntryMediaBlockContainer,
} from '@blocks/media-block/media-block';
import { RichTextEntryImageBlockContainer } from '@blocks/image-block/image-block.styles';
import LogoBlock from '@blocks/logo-block/logo-block';
import CustomBlockHandler from '../custom-block-handler/custom-block-handler';
import {
  H1Atom,
  H2Atom,
  H3Atom,
  H4Atom,
  H5Atom,
  H6Atom,
  PAtom,
  QuoteAtom,
} from '@atoms/text-atoms/text-atoms';
import LegendBlock from '@blocks/legend-block/legend-block';
import FlipCardBlock from '@blocks/flip-card-block/flip-card-block';
import ImageGalleryCarousel from '@blocks/image-gallery-carousel-block/image-gallery-carousel-block';
import MarkdownOpeningParagraph from '@components/markdown-opening-paragraph/markdown-opening-paragraph';

const RichText = ({
  content,
  tooltipData,
  isAccordionOpen = true,
  className,
  extraComponents = {},
}) => {
  const options = {
    renderMark: {
      [MARKS.BOLD]: text => <span className="font-weight-1">{text}</span>,
      [MARKS.ITALIC]: text => <span className="italic">{text}</span>,
      [MARKS.UNDERLINE]: text => <span className="underline">{text}</span>,
    },
    renderNode: {
      [BLOCKS.HEADING_1]: (node, children) => <H1Atom>{children}</H1Atom>,
      [BLOCKS.HEADING_2]: (node, children) => (
        <div className="grid-wrapper">
          <H2Atom>{children}</H2Atom>
        </div>
      ),
      [BLOCKS.HEADING_3]: (node, children) => (
        <div className="grid-wrapper">
          <H3Atom>{children}</H3Atom>
        </div>
      ),
      [BLOCKS.HEADING_4]: (node, children) => <H4Atom>{children}</H4Atom>,
      [BLOCKS.HEADING_5]: (node, children) => <H5Atom>{children}</H5Atom>,
      [BLOCKS.HEADING_6]: (node, children) => <H6Atom>{children}</H6Atom>,
      [BLOCKS.HR]: () => <DashedHrAtom />,
      [BLOCKS.LIST_ITEM]: (node, children) => <li>{children}</li>,
      [BLOCKS.OL_LIST]: (node, children) => (
        <div className="grid-wrapper">
          <OrderedListAtom>{children}</OrderedListAtom>
        </div>
      ),
      [BLOCKS.PARAGRAPH]: (node, children) => {
        return (
          <div className="grid-wrapper">
            <PAtom>
              {children.map((child, keyNumber) => {
                if (
                  isAccordionOpen &&
                  tooltipData &&
                  tooltipData.termsForTooltip &&
                  tooltipData.shouldDisplayTextTooltips &&
                  typeof child === 'string'
                ) {
                  return (
                    <GlossaryTooltipText
                      key={keyNumber}
                      usedTooltipKeywords={tooltipData.usedTooltipKeywords}
                      termsForTooltip={tooltipData.termsForTooltip}
                    >
                      {child}
                    </GlossaryTooltipText>
                  );
                } else {
                  return child;
                }
              })}
            </PAtom>
          </div>
        );
      },
      [BLOCKS.QUOTE]: (node, children) => (
        <div className="grid-wrapper">
          <QuoteAtom>{children}</QuoteAtom>
        </div>
      ),
      [BLOCKS.UL_LIST]: (node, children) => (
        <div className="grid-wrapper">
          <UnorderedListAtom>{children}</UnorderedListAtom>
        </div>
      ),
      [INLINES.HYPERLINK]: (node, children) => (
        <FlexibleLinkAtom
          url={node.data.uri}
          gtmId={node.data.uri ? `inline-${getGtmLinkId(node.data.uri)}` : null}
          isInline={true}
        >
          {children}
        </FlexibleLinkAtom>
      ),
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        try {
          const targetId = node.data.target.sys.id;
          if (content.entryReferences) {
            const linkedEntryData = content.entryReferences.find(
              entryReference => entryReference.contentfulId === targetId
            );
            const slug = linkedEntryData ? linkedEntryData.slug : undefined;
            return (
              <FlexibleLinkAtom
                url={slug}
                gtmId={slug ? `inline-${getGtmLinkId(slug)}` : null}
                isInline={true}
              >
                {children}
              </FlexibleLinkAtom>
            );
          }
        } catch (error) {
          console.error('Error in rich-text.js: ', error);
        }
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
        try {
          const targetId = node.data.target.sys.id;
          if (content.entryReferences) {
            const linkedEntryData = content.entryReferences.find(
              entryReference => entryReference.contentfulId === targetId
            );
            if (
              linkedEntryData?.contentType ===
              'ContentfulRichTextOpeningParagraph'
            ) {
              return (
                <MarkdownOpeningParagraph
                  markdownText={linkedEntryData.markdownText}
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulCallToActionBlock'
            ) {
              return (
                <CallToActionBlock
                  anchorId={linkedEntryData.anchorId}
                  title={linkedEntryData.title}
                  description={linkedEntryData.description}
                  buttonText={linkedEntryData.buttonText}
                  internalUrl={linkedEntryData.internalUrl}
                  externalUrl={linkedEntryData.externalUrl}
                  colour={linkedEntryData.colour}
                  secondButtonText={linkedEntryData.secondButtonText}
                  secondInternalUrl={linkedEntryData.secondInternalUrl}
                  secondExternalUrl={linkedEntryData.secondExternalUrl}
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulMediaBlock'
            ) {
              return (
                <RichTextEntryMediaBlockContainer>
                  <MediaBlock
                    anchorId={linkedEntryData.anchorId}
                    mediaSrc={linkedEntryData.mediaSrc}
                  />
                </RichTextEntryMediaBlockContainer>
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulImageBlock'
            ) {
              return (
                <RichTextEntryImageBlockContainer>
                  <ImageBlock
                    anchorId={linkedEntryData.anchorId}
                    title={linkedEntryData.title}
                    images={linkedEntryData.images}
                    mobileImages={linkedEntryData.mobileImages}
                    colour={linkedEntryData.colour}
                    narrowWidth={linkedEntryData.narrowWidth}
                    firstImageLink={linkedEntryData.firstImageLink}
                    firstImageOpenInNewTab={
                      linkedEntryData.firstImageOpenInNewTab
                    }
                    secondImageLink={linkedEntryData.secondImageLink}
                    secondImageOpenInNewTab={
                      linkedEntryData.secondImageOpenInNewTab
                    }
                  />
                </RichTextEntryImageBlockContainer>
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulHighlightBlock'
            ) {
              return (
                <HighlightBlock
                  anchorId={linkedEntryData.anchorId}
                  quote={linkedEntryData.quote}
                  author={linkedEntryData.author}
                  authorImage={linkedEntryData.authorImage}
                  jobTitleOrganisation={linkedEntryData.jobTitleOrganisation}
                  quoteTextColor={linkedEntryData.quoteTextColor}
                  hasDottedLines={linkedEntryData.hasDottedLines}
                  narrowWidth={linkedEntryData.narrowWidth}
                />
              );
            } else if (linkedEntryData?.contentType === 'ContentfulTileBlock') {
              return (
                <TileBlock
                  anchorId={linkedEntryData.anchorId}
                  title={linkedEntryData.title}
                  description={linkedEntryData.description}
                  cta={linkedEntryData.cta}
                  tiles={linkedEntryData.tiles}
                  colour={linkedEntryData.colour}
                  isCarousel={linkedEntryData.isCarousel}
                  visibleTilesCount={linkedEntryData.visibleTilesCount}
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulInteractiveImageBlock'
            ) {
              return (
                <InteractiveImageBlock
                  title={linkedEntryData.title}
                  image={linkedEntryData.image}
                  imageWidth={linkedEntryData.imageWidth}
                  imageHeight={linkedEntryData.imageHeight}
                  interactiveImagePinBlocks={
                    linkedEntryData.interactiveImagePinBlocks
                  }
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulEmbedBlock'
            ) {
              return (
                <EmbedBlock
                  embedId={linkedEntryData.id}
                  anchorId={linkedEntryData.anchorId}
                  titleCmsOnly={linkedEntryData.titleCmsOnly}
                  title={linkedEntryData.title}
                  displayTitle={linkedEntryData.displayTitle}
                  description={linkedEntryData.description}
                  url={linkedEntryData.url}
                  isFullWidth={linkedEntryData.isFullWidth}
                  mobileHeight={linkedEntryData.mobileHeight}
                  tabletHeight={linkedEntryData.tabletHeight}
                  desktopHeight={linkedEntryData.desktopHeight}
                  colour={linkedEntryData.colour}
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulCircularExamplesBlock'
            ) {
              return (
                <CircularExamplesBlock
                  anchorId={linkedEntryData.anchorId}
                  title={linkedEntryData.title}
                  subtitle={linkedEntryData.subtitle}
                  examples={linkedEntryData.examples}
                  colour={linkedEntryData.colour}
                />
              );
            } else if (
              linkedEntryData?.contentType ===
              'ContentfulAutomatedListingTileBlock'
            ) {
              return (
                <AutomatedListingTileBlock
                  automatedListing={linkedEntryData.automatedListing}
                  localeCode={linkedEntryData.localeCode}
                  pageColour={linkedEntryData.pageColour}
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulAccordionBlock' &&
              extraComponents.Accordion
            ) {
              return (
                <extraComponents.Accordion
                  anchorId={linkedEntryData.anchorId}
                  title={linkedEntryData.title}
                  items={linkedEntryData.items}
                  greyBackground={linkedEntryData.greyBackground}
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulSingleCtaBlock' &&
              extraComponents.SingleCta
            ) {
              return (
                <extraComponents.SingleCta
                  title={linkedEntryData.title}
                  description={linkedEntryData.description}
                  content={linkedEntryData.content}
                  backgroundColor={linkedEntryData.backgroundColor}
                  gatsbyImageData={linkedEntryData.gatsbyImageData}
                  format={linkedEntryData.format}
                  imageSrc={linkedEntryData.imageSrc}
                  imageAlt={linkedEntryData.imageAlt}
                  alignment={linkedEntryData.alignment}
                  linkOnePath={linkedEntryData.linkOnePath}
                  linkOneText={linkedEntryData.linkOneText}
                  isLinkOneExternal={linkedEntryData.isLinkOneExternal}
                  linkTwoPath={linkedEntryData.linkTwoPath}
                  linkTwoText={linkedEntryData.linkTwoText}
                  isLinkTwoExternal={linkedEntryData.isLinkTwoExternal}
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulAlignedImageBlock' &&
              extraComponents.AlignedImage
            ) {
              return (
                <extraComponents.AlignedImage
                  title={linkedEntryData.title}
                  content={linkedEntryData.content}
                  imageSrc={linkedEntryData.imageSrc}
                  imageAlt={linkedEntryData.imageAlt}
                  gatsbyImageData={linkedEntryData.gatsbyImageData}
                  format={linkedEntryData.format}
                  alignImage={linkedEntryData.alignImage}
                  internalUrl={linkedEntryData.internalUrl}
                  mediaUrl={linkedEntryData.mediaUrl}
                  imageLink={linkedEntryData.imageLink}
                  imageOpenInNewTab={linkedEntryData.imageOpenInNewTab}
                  internalImageLink={linkedEntryData.internalImageLink}
                  firstButtonLink={linkedEntryData.firstButtonLink}
                  firstButtonText={linkedEntryData.firstButtonText}
                  secondButtonText={linkedEntryData.secondButtonText}
                  secondButtonLink={linkedEntryData.secondButtonLink}
                />
              );
            } else if (linkedEntryData?.contentType === 'ContentfulLogoBlock') {
              return (
                <LogoBlock
                  title={linkedEntryData.title}
                  anchorId={linkedEntryData.anchorId}
                  logos={linkedEntryData.logos}
                  colour={linkedEntryData.colour}
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulCustomBlock'
            ) {
              return <CustomBlockHandler content={linkedEntryData} />;
            } else if (
              linkedEntryData?.contentType === 'ContentfulLegendBlock'
            ) {
              return (
                <LegendBlock
                  logoSrc={linkedEntryData.logoSrc}
                  companyName={linkedEntryData.companyName}
                  origin={linkedEntryData.origin}
                  founded={linkedEntryData.founded}
                  website={linkedEntryData.website}
                />
              );
            } else if (
              linkedEntryData?.contentType === 'ContentfulFlipcardBlock'
            ) {
              return <FlipCardBlock {...linkedEntryData} />;
            } else if (
              linkedEntryData?.contentType === 'ContentfulGalleryCarouselBlock'
            ) {
              return <ImageGalleryCarousel content={linkedEntryData} />;
            } else {
              return null;
            }
          }
        } catch (error) {
          console.error('Error in rich-text.js: ', error);
        }
      },
    },
  };

  if (content && content.raw) {
    return (
      <div className={className}>
        {content.image && content.image.src && (
          <div className="grid-wrapper">
            <img
              data-testid="rich-text-image"
              src={content.image.src}
              alt={content.image.alt ? content.image.alt : ''}
            />
          </div>
        )}

        {renderRichText(content, options)}
      </div>
    );
  }

  if (content && content.glossaryTermAccordion) {
    return (
      <div className={className}>
        <PAtom>{content.glossaryTermAccordion.definition}</PAtom>
        <PAtom>
          <span className="italic">
            {content.glossaryTermAccordion.supportingDefinition}
          </span>
        </PAtom>
      </div>
    );
  }
  console.warn(`RichText Component called with null data`);
  return null;
};

const StyledRichText = styled(RichText)``;

export default StyledRichText;
